import { useEffect, useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { PageContent } from '../../components/templates/PageContent';
import { PageHeader } from '../../components/templates/PageHeader';
import { Section } from '../../components/templates/Section';
import { Card } from '../../components/atoms/Card';
import { Stack } from '../../components/atoms/Stack';
import { Inline } from '../../components/atoms/Inline';
import { Badge, type BadgeTone } from '../../components/atoms/Badge';
import { Button } from '../../components/atoms/Button';
import { Icon } from '../../components/atoms/Icon/Icon';
import { Heading, Text } from '../../components/atoms/Typography';
import { EmptyState } from '../../components/atoms/EmptyState';
import { LiveRegion } from '../../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../../components/feedback/LoadErrorState/LoadErrorState';
import { Alert } from '../../components/atoms/Alert';
import { Dialog } from '../../components/ui/Dialog/Dialog';
import { useToast } from '../../stores/toastStore';
import { useDeleteZeovitLineage } from '../../hooks/useZeovitRecommendation';
import { groupLineages } from '../../utils/zeovitLineage';
import { useTranslation } from '../../i18n/useTranslation';
import { useZeovitRecommendations } from '../../hooks/useZeovitRecommendations';
import { useZeovitAvailability } from '../../hooks/useZeovitAvailability';
import { ZeovitUnavailableNotice } from '../../components/zeovit/ZeovitUnavailableNotice';
import type {
  ZeovitEffectClassification,
  ZeovitRecommendationListItem,
  ZeovitRecommendationStatus,
} from '../../services/zeovitRecommendationService';
import './IterationHistoryPage.css';

const HISTORY_HEADING_ID = 'zeovit-history-title';

/** Status → Badge tone + decorative icon. The Badge text always carries the meaning. */
const STATUS_PRESENTATION: Record<ZeovitRecommendationStatus, { tone: BadgeTone; icon: string }> = {
  pending: { tone: 'info', icon: 'clock' },
  adopted: { tone: 'success', icon: 'check' },
  superseded: { tone: 'neutral', icon: 'history' },
  capped: { tone: 'warning', icon: 'alert-triangle' },
};

/** Effect classification → Badge tone + decorative icon (text carries the meaning). */
const CLASSIFICATION_PRESENTATION: Record<ZeovitEffectClassification, { tone: BadgeTone; icon: string }> = {
  darkening: { tone: 'warning', icon: 'alert-triangle' },
  yellowing_paling: { tone: 'warning', icon: 'alert-triangle' },
  browning: { tone: 'info', icon: 'sun' },
  no_change: { tone: 'neutral', icon: 'minus' },
};

function toTime(iso: string): number {
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/** Newest first — same order as the API (createdAt desc, then iteration desc). */
function sortNewestFirst(items: readonly ZeovitRecommendationListItem[]): ZeovitRecommendationListItem[] {
  return [...items].sort(
    (a, b) => toTime(b.createdAt) - toTime(a.createdAt) || b.iterationNumber - a.iterationNumber,
  );
}

function formatDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

interface IterationCardProps {
  item: ZeovitRecommendationListItem;
  /** Omitted in the read-only history of a non-ZeoVit aquarium (no link into the module). */
  onView?: (recommendationId: string) => void;
}

function IterationCard({ item, onView }: IterationCardProps) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const status = STATUS_PRESENTATION[item.status];
  const classification = item.effectClassification
    ? CLASSIFICATION_PRESENTATION[item.effectClassification]
    : null;
  const date = formatDate(item.createdAt, i18n.language);

  return (
    <li>
      <Card data-testid="iteration-history-row">
        <Stack gap="2">
          <Inline justify="between">
            <Heading level={3} id={headingId}>
              {t('zeovit.history.item.iteration', { number: item.iterationNumber })}
            </Heading>
            <Badge tone={status.tone}>
              <Icon name={status.icon} size="sm" />
              {t(`zeovit.status.${item.status}`)}
            </Badge>
          </Inline>
          {date ? (
            <Text color="muted">
              <time dateTime={item.createdAt}>{t('zeovit.history.item.date', { date })}</time>
            </Text>
          ) : null}
          {classification && item.effectClassification ? (
            <Inline>
              <Badge tone={classification.tone}>
                <Icon name={classification.icon} size="sm" />
                {t('zeovit.history.item.classification', {
                  classification: t(`zeovit.history.classification.${item.effectClassification}`),
                })}
              </Badge>
            </Inline>
          ) : null}
          {onView ? (
            <Inline>
              <Button
                variant="ghost"
                aria-describedby={headingId}
                onClick={() => {
                  onView(item.id);
                }}
              >
                {t('zeovit.history.item.view')}
              </Button>
            </Inline>
          ) : null}
        </Stack>
      </Card>
    </li>
  );
}

export function IterationHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  const deleteLineage = useDeleteZeovitLineage(aquariumId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [focusHeading, setFocusHeading] = useState(false);

  // After a delete the trigger may be gone (empty state): move focus to the page
  // title once the dialog has closed and restored its own focus.
  useEffect(() => {
    if (focusHeading && !isDeleteDialogOpen) {
      document.getElementById(HISTORY_HEADING_ID)?.focus();
      setFocusHeading(false);
    }
  }, [focusHeading, isDeleteDialogOpen]);

  const { data, isLoading, isError, isFetching, refetch } = useZeovitRecommendations(aquariumId);
  const availability = useZeovitAvailability(aquariumId);
  // Not a ZeoVit aquarium (#1784): info notice, no actions; recommendations created
  // earlier stay listed read-only beneath it (the GET routes stay open).
  const readOnly = availability === 'unavailable';

  const handleStartNew = () => {
    if (!aquariumId) return;
    navigate(`/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations/new`);
  };

  const handleViewDetail = (recommendationId: string) => {
    if (!aquariumId) return;
    navigate(
      `/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations/${encodeURIComponent(recommendationId)}`,
    );
  };

  const items = sortNewestFirst(data ?? []);
  const isPopulated = !isLoading && !isError && items.length > 0;
  const lineages = groupLineages(items);
  const current = lineages[0] ?? null;
  const earlier = lineages.slice(1).flatMap((lineage) => lineage.items);

  const openDeleteDialog = () => {
    deleteLineage.reset();
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLineage.isPending) return;
    setIsDeleteDialogOpen(false);
  };

  const confirmDelete = () => {
    if (!current) return;
    deleteLineage.mutate(current.rootId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setFocusHeading(true);
        showToast(t('zeovit.history.deleted'), 'success');
      },
    });
  };

  let body;
  if (isLoading || availability === 'loading') {
    body = (
      <LiveRegion busy>
        <Text color="muted">{t('zeovit.history.loading')}</Text>
      </LiveRegion>
    );
  } else if (isError) {
    body = (
      <LoadErrorState
        title={t('zeovit.history.error.loadFailed')}
        onRetry={() => {
          void refetch();
        }}
        retryLabel={t('zeovit.history.retry')}
        retrying={isFetching}
      />
    );
  } else if (readOnly && items.length === 0) {
    body = null;
  } else if (items.length === 0) {
    body = (
      <Card>
        <EmptyState
          icon={<Icon name="flask" size="lg" />}
          headline={t('zeovit.history.empty.title')}
          description={t('zeovit.history.empty.description')}
          actionLabel={t('zeovit.history.startNew')}
          onAction={handleStartNew}
        />
      </Card>
    );
  } else {
    body = (
      <Stack gap="8">
        <Section
          title={t('zeovit.history.listSection')}
          actions={
            readOnly ? undefined : (
              <Button variant="secondary" onClick={openDeleteDialog} data-testid="zeovit-history-delete">
                {t('zeovit.history.delete')}
              </Button>
            )
          }
        >
          <Stack as="ul" gap="4" className="zeovit-history__list">
            {(current?.items ?? []).map((item) => (
              <IterationCard key={item.id} item={item} onView={readOnly ? undefined : handleViewDetail} />
            ))}
          </Stack>
        </Section>
        {earlier.length > 0 ? (
          <Section title={t('zeovit.history.earlierSection')}>
            <Stack as="ul" gap="4" className="zeovit-history__list">
              {earlier.map((item) => (
                <IterationCard key={item.id} item={item} onView={readOnly ? undefined : handleViewDetail} />
              ))}
            </Stack>
          </Section>
        ) : null}
      </Stack>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader
            title={t('zeovit.history.title')}
            description={t('zeovit.history.description')}
            headingProps={{ id: HISTORY_HEADING_ID, tabIndex: -1 }}
            actions={
              isPopulated && !readOnly ? (
                <Button variant="primary" onClick={handleStartNew}>
                  {t('zeovit.history.startNew')}
                </Button>
              ) : undefined
            }
          />
          {readOnly && aquariumId ? (
            <Stack gap="8">
              <ZeovitUnavailableNotice aquariumId={aquariumId} />
              {body}
            </Stack>
          ) : (
            body
          )}
          <Dialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDeleteDialog}
            title={t('zeovit.history.deleteDialog.title')}
            size="sm"
            data-testid="zeovit-history-delete-dialog"
          >
            <Stack gap="3">
              <Text>{t('zeovit.history.deleteDialog.body', { count: current?.items.length ?? 0 })}</Text>
              <Text>{t('zeovit.history.deleteDialog.plansKept')}</Text>
              {deleteLineage.isError ? (
                <Alert tone="danger" compact>
                  {t('zeovit.history.deleteDialog.error')}
                </Alert>
              ) : null}
              <Inline gap="2" wrap>
                <Button
                  variant="danger"
                  onClick={confirmDelete}
                  isLoading={deleteLineage.isPending}
                  disabled={deleteLineage.isPending}
                >
                  {t('zeovit.history.deleteDialog.confirm')}
                </Button>
                <Button variant="secondary" onClick={closeDeleteDialog} disabled={deleteLineage.isPending}>
                  {t('zeovit.history.deleteDialog.cancel')}
                </Button>
              </Inline>
            </Stack>
          </Dialog>
        </PageContent>
      }
    />
  );
}

export default IterationHistoryPage;
